/* eslint-disable camelcase */
import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';
import moment from 'moment';
import { History } from 'history';
import DelegateJobContainer from './delegate/DelegateJobContainer';
import ShiftTableSubComponent from './ShiftTableSubComponent';
import StatusDropDown from './StatusDropDown';
import CollapsibleTable from '../bookings/CollapsibleTable';
import { getBookingStatusString } from '../bookings/table';
import DateDropDown from '../common/datedropdown';
import { withPushFiltersToUrl, withPutUrlSearchFiltersToProps } from '../common/decorators';
import { ApiDropDown, ServiceDropDown, MatchingDropDown } from '../common/dropdowns';
import SearchBar from '../common/SearchBar';
import { BulkActions } from '../freelancers/BulkActions';
import ActionButton from '../../common/ActionButton';
import RefreshButton from '../../common/refreshbutton';
import { SelectRole } from '../../common/SelectRole';
import StatusCell from '../../common/StatusCell';
import Tooltip from '../../common/Tooltip';
import { SERVER_URL } from '../../../config';
import { MASS_BOOKING_MODE, matchingModeCodes, matchingModeLabels } from '../../../consts';
import * as api from '../../../epics/api';
import history from '../../../history';
import { TJobItem, TUser, TMeta, TSeries, TMatchingMode, TPageNotification } from '../../../types';
import { findKeyByValue } from '../../../utils';

export type TShiftTableProps = {
    user: TUser;
    setSelectedFreelancers: (freelancers: any[]) => void;
    setSelectedJobs: (jobs: TJobItem[]) => void;
    onShiftsSelected: (shift: any) => void;
    reloadData: () => void;
    onFilterChange: () => void;
    onPageChange: () => void;
    onSortedChange: () => void;
    isMobile: boolean;
    history: History;
    notification: TPageNotification;
    selectedJobs: TJobItem[];
    selectedSeries: TSeries;
    data: any;
    seriesValue: string | null;
    searchValue: string | null;
    currentPage: number;
    pages: number;
    loading: boolean;
    showPaginationBottom: boolean;
    meta: TMeta;
};

type TShiftTableState = {
    selected: TJobItem[];
    showFilters: boolean;
    showDelegateJobs: boolean;
    isBulkMode: boolean;
    isBulkExclusiveMode: boolean;
    lastCheckedRowIndex: number;
    lastCheckedRowArrayIndex: number;
    isModalConfirmCancelShiftOpen: boolean;
};

type TRow = { original: TJobItem; value: string };
// @ts-ignore TODO - typing of decorators
@withPushFiltersToUrl
// @ts-ignore TODO - typing of decorators
@withPutUrlSearchFiltersToProps
export default class ShiftTable extends Component<
    TShiftTableProps,
    TShiftTableState
> {
    constructor(props: TShiftTableProps) {
        super(props);

        this.state = {
            selected: [],
            showFilters: false,
            showDelegateJobs: false,
            isBulkMode: false,
            isBulkExclusiveMode: false,
            lastCheckedRowIndex: null,
            lastCheckedRowArrayIndex: null,
            isModalConfirmCancelShiftOpen: false,
        };
    }

    componentDidMount() {
        this.props.setSelectedFreelancers([]);
        this.props.setSelectedJobs([]);
    }

    getChildContext = () => ({ user: this.props.user });

    toggleShowFilters = () => {
        this.setState(prevState => ({
            ...prevState,
            showFilters: !prevState.showFilters,
        }));
    };

    onCreate = () => {
        this.props.onShiftsSelected(this.state.selected);
        // eslint-disable-next-line no-shadow
        const { user, history } = this.props;
        const currentUrlParams = new URLSearchParams(history.location.search);
        if (
            user.isClient ||
            (user.isAgent && user.agency.features.includes('booking_tunnel'))
        ) {
            history.push('/job/create');
        } else {
            history.push({
                pathname: '/jobs/list/create',
                search: currentUrlParams.toString(),
            });
        }
    };

    onCancel = () => {
        this.props.onShiftsSelected(this.state.selected);
        history.push('/jobs/list/cancel');
    };

    onGetJobSheet = (row: TRow) => {
        history.push(`/jobs/list/jobdetail/'${row.original.id}`);
    };

    updateJobStatus = (jobId: number, status: string, data: object = {}) => {
        api.updateJobStatus({ jobId, status, data })
            .then(() => {
                this.props.reloadData();
            })
            .catch(() => {
                this.props.notification.error(
                    'Error',
                    'Could not change the status of the job',
                );
            });
    };

    delegateJob = (jobId: number) => {
        const currentUrlParams = new URLSearchParams(history.location.search);
        history.push({
            pathname: `/jobs/list/delegate/${jobId}`,
            search: currentUrlParams.toString(),
        });
    }

    duplicateJob = (jobId: number) => {
        const currentUrlParams = new URLSearchParams(history.location.search);
        history.push({
            pathname: `/jobs/list/duplicate/${jobId}`,
            search: currentUrlParams.toString(),
        });
    };

    editJob = (jobId: number) => {
        const currentUrlParams = new URLSearchParams(history.location.search);
        if (this.props.user.isClient) {
            history.push(`/job/edit/${jobId}`);
        } else {
            history.push({
                pathname: `/jobs/list/edit/${jobId}`,
                search: currentUrlParams.toString(),
            });
        }
    };

    viewJob = (jobId: number) => {
        window.location.assign(`${SERVER_URL}/job/${jobId}`);
    };

    loadCheckoutLink = (jobId: number) => {
        history.push(`/jobs/list/check-out-link/${jobId}`);
    };

    canExpandRow = (job: TJobItem) => this.props.user.isAgent || (job && job.is_editable);

    onRowCheckboxChange = (rowInfo: TRow, checked: boolean, shiftKeyPressed: boolean) => {
        const { lastCheckedRowIndex, lastCheckedRowArrayIndex, isBulkExclusiveMode } = this.state;

        const rowIndex = rowInfo.original.id;
        const rowArrayIndex = this.props.data.findIndex(
            (object: { id: number; }) => object.id === rowIndex
        );
        let allSelectedJobs;

        if (shiftKeyPressed && this.props.selectedJobs.length > 0) {
            // handle selection with 'shift' key
            const start = Math.min(lastCheckedRowArrayIndex, rowArrayIndex);
            const end = Math.max(lastCheckedRowArrayIndex, rowArrayIndex);

            const rowSelectedJobs = this.props.data.filter(
              (_: never, index: number) => index >= start && index <= end
            );

            if (this.props.selectedJobs.some(
                job => job.id === lastCheckedRowIndex)
            ) {
                allSelectedJobs = new Set([
                    ...this.props.selectedJobs,
                    ...rowSelectedJobs
                ]);
            } else {
                allSelectedJobs = this.props.selectedJobs.filter(
                    job => !rowSelectedJobs.some(
                        (selected: { id: number; }) => selected.id === job.id
                    )
                );
            }

            allSelectedJobs = Array.from(allSelectedJobs);

        } else if ((!isBulkExclusiveMode && checked) || (isBulkExclusiveMode && !checked)) {
                allSelectedJobs = this.props.selectedJobs.concat(
                    rowInfo.original,
                );
            } else {
                allSelectedJobs = this.props.selectedJobs.filter(
                    item => item.id !== rowInfo.original.id,
                );
            }

        this.setState({
            lastCheckedRowIndex: rowIndex,
            lastCheckedRowArrayIndex: rowArrayIndex,
        });
        this.props.setSelectedJobs(allSelectedJobs);
    };

    onToggleBulkMode = () => {
        this.setState((prevState: TShiftTableState) => ({
            isBulkMode: !prevState.isBulkMode,
            isBulkExclusiveMode: false,
        }));
    };

    onBulkAction = (key: string, val: { value: string }) => {
        if (val.value === 'match_workers') {
            const filterDisplaysOnlyPublishedFreelancers =
                '&worker_status=published&published=1&is_active=1&page=1';
            history.push(
                `/freelancers?mode=${MASS_BOOKING_MODE}${filterDisplaysOnlyPublishedFreelancers}`,
            );
        } else if (val.value === 'delegate_jobs') {
            this.setState({
                showDelegateJobs: true,
            });
        }
    };

    selectAllRows = () => {
        let allSelectedJobs;
        const allJobs = this.props.data;
        allSelectedJobs = new Set([
            ...this.props.selectedJobs,
            ...allJobs
        ]);
        allSelectedJobs = Array.from(allSelectedJobs);
        this.props.setSelectedJobs(allSelectedJobs);
    }

    deselectAllRows = () => {
        this.props.setSelectedJobs([]);
    }

    isRowChecked = (rowInfo: TRow) => {
        const { isBulkExclusiveMode } = this.state;
        const isInSelectedJobs = this.props.selectedJobs.some(
            item => item.id === rowInfo.original.id,
        );
        return isBulkExclusiveMode ? !isInSelectedJobs : isInSelectedJobs;
    };

    chooseMatchingLabel = (matchingMode: TMatchingMode) =>
        // @ts-ignore
        matchingModeLabels[findKeyByValue(matchingModeCodes, matchingMode)];

    renderDropDownFilters = () => {
        const currentUrlParams = new URLSearchParams(history.location.search);

        return (
            <div className="table-filter-container-outer">
                <div className="table-filter-container hidable">
                    <div className="table-action-bar filters">
                        <div className="grouped-buttons">
                            {!!this.props.isMobile && (
                                <ActionButton
                                    className="filter"
                                    onAction={this.toggleShowFilters}
                                    iconClass="icon-filter-22"
                                    active={this.state.showFilters}
                                    tooltip={
                                        !this.props.isMobile &&
                                        this.state.showFilters
                                            ? 'Hide Filters'
                                            : 'Filters'
                                    }
                                />
                            )}
                            <DateDropDown
                                history={history}
                                onFilterChange={this.props.onFilterChange}
                                data={this.props.data}
                            />
                            <StatusDropDown
                                onFilterChange={this.props.onFilterChange}
                                history={history}
                            />
                            <SelectRole
                                onFilterChange={this.props.onFilterChange}
                                currentUrlParams={currentUrlParams}
                            />
                            {this.props.user.isAgent && (
                                <MatchingDropDown
                                    onFilterChange={this.props.onFilterChange}
                                />
                            )}
                            {this.props.user.isAgent && (
                                <ApiDropDown
                                    onFilterChange={this.props.onFilterChange}
                                    currentUrlParams={currentUrlParams}
                                    url={`agencies/${this.props.user.agency.id}/agents/`}
                                    filterKey='consultant'
                                    labelKey='full_name'
                                    valueKey='id'
                                    verboseNamePlural='consultants'
                                />
                            )}
                            {this.props.user.isAgent && (
                                <ApiDropDown
                                    onFilterChange={this.props.onFilterChange}
                                    currentUrlParams={currentUrlParams}
                                    url='cost-centres/'
                                    filterKey='cost_centre'
                                    labelKey='code'
                                    valueKey='id'
                                    verboseNamePlural='cost centres'
                                />
                            )}
                            <SearchBar
                                filterKey="search"
                                onFilterChange={this.props.onFilterChange}
                                currentUrlParams={currentUrlParams}
                                value={this.props.searchValue}
                            />
                            <SearchBar
                                filterKey="series"
                                onFilterChange={this.props.onFilterChange}
                                currentUrlParams={currentUrlParams}
                                customPlaceHolder="Search for series..."
                                isSearchBySeries={true}
                                value={this.props.seriesValue}
                            />
                        </div>
                    </div>

                    <div className="table-action-bar">
                        <BulkActions
                            user={this.props.user}
                            items={this.props.selectedJobs}
                            onBulkAction={this.onBulkAction}
                            totalNumberOfItemsInTable={this.props.meta?.count}
                            isBulkExclusiveMode={this.state.isBulkExclusiveMode}
                            isBulkMode={this.state.isBulkMode}
                            selectedJobs={this.props.selectedJobs}
                            selectedSeries={this.props.selectedSeries}
                            selectAllRows={this.selectAllRows}
                            deselectAllRows={this.deselectAllRows}
                        />
                        <RefreshButton
                            reloadData={this.props.reloadData}
                            isMobile={this.props.isMobile}
                        />
                        <div className="grouped-buttons">
                            <ActionButton
                                onAction={() => this.onCreate()}
                                text={
                                    !this.props.isMobile &&
                                    this.props.user.isClient
                                        ? 'New booking'
                                        : 'Create'
                                }
                                tooltip={
                                    !this.props.isMobile &&
                                    this.props.user.isClient
                                        ? 'New booking'
                                        : 'Create'
                                }
                                requiredPermission="job.add_jobrequest"
                                iconClass="icon-add-12"
                            />
                            <ActionButton
                                tooltip="List view"
                                active={true}
                                iconClass="icon-view-list"
                            />
                        </div>
                    </div>
                    {this.state.showDelegateJobs &&
                        <DelegateJobContainer
                            jobrequestIds={this.props.selectedJobs.map(job => job.id)}
                            onCancel={() => this.setState({ showDelegateJobs: false })}
                            notification={this.props.notification}
                        />
                    }
                </div>
                {!!this.state.showFilters && (
                    <div className="table-action-bar">
                        <StatusDropDown
                            onFilterChange={this.props.onFilterChange}
                            history={history}
                        />
                        <ServiceDropDown
                            onFilterChange={this.props.onFilterChange}
                        />
                        <SelectRole
                            onFilterChange={this.props.onFilterChange}
                            currentUrlParams={currentUrlParams}
                        />
                        <SearchBar
                            filterKey="search"
                            onFilterChange={this.props.onFilterChange}
                            currentUrlParams={currentUrlParams}
                            value={this.props.searchValue}
                        />
                        <SearchBar
                            filterKey="series"
                            onFilterChange={this.props.onFilterChange}
                            currentUrlParams={currentUrlParams}
                            customPlaceHolder="Search for series..."
                            isSearchBySeries={true}
                            value={this.props.seriesValue}
                        />
                    </div>
                )}
            </div>
        );
    };

    handleOpenModal = (modalName: string) => {
        this.setState(prevState => ({
            ...prevState,
            [`isModal${modalName}Open`]: true,
        }));
    }

    handleCloseModal = (modalName: string) => {
        this.setState(prevState => ({
            ...prevState,
            [`isModal${modalName}Open`]: false,
        }));
    }

    render = () => (
        <div className="shifts table-container">
            {this.renderDropDownFilters()}
            <CollapsibleTable
                page={this.props.currentPage}
                pageSize={0}
                data={this.props.data}
                pages={this.props.pages}
                columns={this.columns}
                showPaginationBottom={this.props.showPaginationBottom}
                className="shifts table-content"
                loading={this.props.loading}
                showPageSizeOptions={false}
                manual={true}
                onPageChange={this.props.onPageChange}
                onSortedChange={this.props.onSortedChange}
                onRowCheckboxChange={this.onRowCheckboxChange}
                onToggleBulkMode={this.onToggleBulkMode}
                isBulkMode={this.state.isBulkMode}
                bulkModeEnabled={this.props.user.isAgent}
                isRowChecked={this.isRowChecked}
                expandUsingURLPrimaryKey={false}
                canExpandRow={this.canExpandRow}
                SubComponent={(row: TRow) => (
                    <ShiftTableSubComponent
                        job={row.original}
                        user={this.props.user}
                        updateJobStatus={this.updateJobStatus}
                        delegateJob={this.delegateJob}
                        editJob={this.editJob}
                        duplicateJob={this.duplicateJob}
                        loadCheckoutLink={this.loadCheckoutLink}
                        viewJob={this.viewJob}
                        handleOpenModal={this.handleOpenModal}
                        handleCloseModal={this.handleCloseModal}
                        isModalConfirmCancelShiftOpen={this.state.isModalConfirmCancelShiftOpen}
                    />
                )}
            />
        </div>
    );

    columns = [
        {
            Header: 'Ref',
            accessor: 'reference_number',
            id: 'reference_number',
            headerClassName: 'small-col',
            className: 'small-col',
            sortable: false,
            Cell: (row: TRow) => {
                if (
                    (this.props.user.isAgent && !row.original.owned) ||
                    (this.props.user.isAgent && !row.original.managed)
                ) {
                    let tooltipMessage;
                    if (row.original.owned && !row.original.managed) {
                        tooltipMessage = `This job is managed by ${row.original.managing_agency_details.company_name}`;
                    } else {
                        tooltipMessage = `This job is delegated by ${row.original.owner_agency_details?.company_name}`;
                    }
                    return (
                        <a href={`${SERVER_URL}/job/${row.original.id}`}>
                            {row.value}
                            <Tooltip tooltip={<span>{tooltipMessage}</span>}>
                                <i className="icon icon-infomation-circle margin-5-px" />
                            </Tooltip>
                        </a>
                    );
                }
                if (row.original.generated_from) {
                    const tooltipMessage = `This subscription job generated from ${row.original.generated_from}`;
                    return (
                        <a href={`${SERVER_URL}/job/${row.original.id}`}>
                            {row.value}
                            <Tooltip tooltip={<span>{tooltipMessage}</span>}>
                                <i className="icon icon-synchronize-6 margin-5-px" />
                            </Tooltip>
                        </a>
                    );
                }
                return (
                    <a href={`${SERVER_URL}/job/${row.original.id}`}>
                        {row.value}
                        {row.original.additional_services &&
                            <Tooltip tooltip={<span>Multi-sector job</span>}>
                                <i className="icon icon-check-double-22 margin-5-px" />
                            </Tooltip>
                        }
                    </a>
                );
            },
        },
        {
            Header: 'Assignment',
            accessor: 'series_reference_number',
            headerClassName: 'small-col',
            className: 'small-col',
            sortable: false,
        },
        {
            Header: 'Client',
            accessor: 'client_full.company_name',
            show: this.props.user.isAgent,
            sortable: false,
        },
        {
            Header: 'Date',
            accessor: 'date',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TRow) => (
                <span>{moment(row.value).format('DD/MM/YYYY')}</span>
            ),
        },
        {
            Header: 'Start',
            accessor: 'start_time',
            headerClassName: 'xsmall-col',
            className: 'xsmall-col',
            Cell: (row: TRow) => moment(row.value, 'HH:mm:ss').format('HH:mm'),
        },
        {
            Header: 'Hours',
            accessor: 'duration',
            headerClassName: 'xsmall-col',
            className: 'xsmall-col',
            Cell: (data: TRow) => {
                const { original: shift } = data;
                const hasBooking = shift.booking !== undefined;
                if (
                    hasBooking &&
                    getBookingStatusString(shift) === 'Completed'
                ) {
                    const startDate = moment(shift.booking.datetime_started);
                    const finishDate = moment(shift.booking.datetime_finished);
                    const totalHours = parseFloat(
                        finishDate.diff(startDate, 'hours', true).toFixed(2),
                    );
                    if (totalHours % 1 > 0.9) {
                        return Math.ceil(totalHours);
                    }
                    if (totalHours % 1 < 0.1) {
                        return Math.floor(totalHours);
                    }
                    return totalHours;
                }
                return data.value;
            },
        },
        {
            Header: 'Location',
            accessor: 'postcode',
            headerClassName: 'small-col',
            className: 'small-col',
        },
        {
            Header: 'Worker',
            accessor: 'Name',
            sortable: false,

            Cell: (data: TRow) => {
                const freelancer = data.original.booking
                    ? data.original.booking.freelancer_detail
                    : null;
                if (freelancer) {
                    return (
                        <NavLink to={`/operations/freelancer/${freelancer.id}`}>
                            {freelancer.full_name}
                        </NavLink>
                    );
                }

                // Use the new assigned_workers_count field from the API
                const assignedWorkers = data.original.assigned_workers_count || 0;
                const totalWorkers = data.original.number_of_freelancers;

                const { additional_services } = data.original;
                let {
                    number_of_freelancers_to_be_invited,
                    number_of_invited_freelancers,
                    number_of_applied_freelancers,
                } = data.original;

                if (additional_services) {
                    additional_services.forEach(childJob => {
                        number_of_freelancers_to_be_invited += childJob.number_of_freelancers_to_be_invited;
                        number_of_invited_freelancers += childJob.number_of_invited_freelancers;
                        number_of_applied_freelancers += childJob.number_of_applied_freelancers;
                    });
                }

                return (
                    <Tooltip
                        tooltip={
                            <span>
                                <p>
                                    Number of required freelancers:{' '}
                                    {totalWorkers}
                                </p>
                                {number_of_freelancers_to_be_invited ? (
                                    <p>
                                        Invitations sent:{' '}
                                        {number_of_invited_freelancers}{' '}
                                        /{' '}
                                        {number_of_freelancers_to_be_invited}
                                    </p>
                                ) : null}
                                {number_of_applied_freelancers ? (
                                    <p>
                                        Total applied:{' '}{number_of_applied_freelancers}
                                    </p>
                                ) : null}
                            </span>
                        }
                    >
                        <span>
                            <>
                                {assignedWorkers}/{totalWorkers}
                            </>
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            Header: 'Title',
            accessor: 'title',
            sortable: false,
            Cell: (row: TRow) => (
                <Tooltip tooltip={<span>{row.value}</span>}>
                    <span>{row.value}</span>
                </Tooltip>
            ),
        },
        {
            Header: 'Service',
            accessor: 'service_role',
            sortable: false,
            Cell: (row: TRow) => {
                const parentServiceRole = row.original.role === 'MX'
                    ? 'Hospitality general'
                    : row.original.service_role;

                const childServiceRoles = row.original.additional_services
                    ? `; ${row.original.additional_services.map(childJob => 
                        childJob.role === 'MX'
                            ? 'Hospitality general'
                            : childJob.service_role
                    ).join('; ')}`
                    : '';

                const serviceRole = <span>{parentServiceRole}{childServiceRoles}</span>;

                return <Tooltip tooltip={serviceRole}>{serviceRole}</Tooltip>;
            },
        },
        {
            Header: 'Status',
            accessor: 'status',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TRow) => (
                <StatusCell
                    jobStatus={row.value}
                    booking={row.original.booking}
                    additionalServices={row.original.additional_services}
                    delegated={row.original.owned && !row.original.managed}
                />
            ),
        },
        {
            Header: 'Matching',
            accessor: 'matching',
            headerClassName: 'small-col',
            className: 'small-col',
            show: this.props.user.isAgent,
            Cell: (row: { original: TJobItem }) => (
                <span>
                    {this.chooseMatchingLabel(row.original.matching_mode)}
                </span>
            ),
        },
    ];
}
